import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import GlobalLogo from '@/components/feature/GlobalLogo';

export default function LoginPage() {
  const { signIn } = useAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    if (!email.trim() || !password.trim()) {
      setError('Completá todos los campos.');
      return;
    }

    setLoading(true);
    const { error: authError } = await signIn(email.trim(), password);
    setLoading(false);

    if (authError) {
      setError(authError.message || 'Error al iniciar sesión.');
      return;
    }

    navigate('/');
  };

  return (
    <div className="min-h-screen bg-background-50 flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-block">
            <GlobalLogo />
          </Link>
          <h1 className="text-2xl font-semibold text-foreground-950 mt-6">
            Iniciar sesión
          </h1>
          <p className="text-sm text-foreground-600 mt-2">
            Entrá a tu cuenta de RadioFrecuenciaWeb
          </p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="bg-background-50 border border-background-200/70 rounded-lg p-6 space-y-5"
        >
          {error && (
            <div className="bg-accent-100 text-accent-900 text-sm px-4 py-3 rounded-md border border-accent-200">
              {error}
            </div>
          )}

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
              Email
            </label>
            <input
              id="email"
              type="email"
              name="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-2.5 text-sm border border-background-200 rounded-md bg-background-50 text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition"
              placeholder="tu@email.com"
              disabled={loading}
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label htmlFor="password" className="block text-sm font-medium text-foreground-700">
                Contraseña
              </label>
              <Link
                to="/reset-password"
                className="text-xs text-primary-500 hover:text-primary-600 transition whitespace-nowrap"
              >
                ¿La olvidaste?
              </Link>
            </div>
            <input
              id="password"
              type="password"
              name="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-2.5 text-sm border border-background-200 rounded-md bg-background-50 text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition"
              placeholder="Tu contraseña"
              disabled={loading}
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-2.5 px-4 bg-primary-500 text-background-50 text-sm font-medium rounded-md hover:bg-primary-600 transition disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
          >
            {loading ? 'Entrando...' : 'Entrar'}
          </button>
        </form>

        <p className="text-center text-sm text-foreground-600 mt-6">
          ¿No tenés cuenta?{' '}
          <Link to="/register" className="text-primary-500 hover:text-primary-600 font-medium transition">
            Registrate
          </Link>
        </p>
      </div>
    </div>
  );
}